import {
  Case,
  QueueConfiguration,
  assignCases,
  deleteReviewer,
  postReviewer,
  unassignCases,
  ReleaseCaseRequest,
  claimCase
} from '../../services';
import { LocationState, UnregisterCallback } from 'history';
import { MessageBar, MessageBarType } from '@fluentui/react/lib/MessageBar';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { Spinner, SpinnerSize } from '@fluentui/react/lib/Spinner';
import React from 'react';
import { formatEmailHref } from '../util/feedback-email';
import { TitleBar } from '../navigation';
import { getTheme, Stack, StackItem } from '@fluentui/react';
import { ReviewContent } from './ReviewContent';
import { ReleaseCaseData } from 'services';
import { ReviewContextProvider, CaseReview } from './ReviewContextProvider';
import { ReviewOpinionForm } from './ReviewOpinionForm';
import { generateReleaseData } from 'components/util';
import { NextCaseModal } from 'components/shared';
import { StateITF } from '../admin';
import { borderLeftStyle } from '../shared/BorderStyles';

export interface ReviewState {
  isLoading: boolean;
  submittingReview: boolean;
  isServiceError: boolean;
  isTimerExpired: boolean;
  errorMessage: string | undefined;
  config: QueueConfiguration;
  cases: Case[];
  userStatDocId: string | undefined;
  caseId: string | undefined;
}

export class Review extends React.Component<RouteComponentProps, ReviewState> {
  private historyCallback: UnregisterCallback;

  constructor(props: RouteComponentProps) {
    super(props);

    if (
      this.props.location == null ||
      this.props.location.state == null ||
      (this.props.location.state as StateITF).passedQueueData == null
    ) {
      this.props.history.push('/');
    } else {
      const config = (this.props.location.state as StateITF).passedQueueData;
      this.state = {
        config,
        isLoading: true,
        submittingReview: false,
        isServiceError: false,
        isTimerExpired: false,
        errorMessage: undefined,
        cases: [],
        userStatDocId: undefined,
        caseId: (this.props.location.state as StateITF).caseId as string
      };
    }

    // Listen for navigation events to refresh view
    this.historyCallback = this.props.history.listen((location: LocationState) => {
      if (
        location == null ||
        // @ts-ignore
        location.state == null ||
        // @ts-ignore
        location.state.passedQueueData == null ||
        // @ts-ignore
        location.state.passedQueueData.id === this.state.config.id
      ) {
        return;
      }

      // @ts-ignore
      const config = location.state.passedQueueData;
      this.setState({
        config,
        isLoading: true,
        isServiceError: false,
        isTimerExpired: false,
        errorMessage: undefined,
        cases: []
      });
    });
  }

  componentWillUnmount(): void {
    this.historyCallback();
  }

  async componentDidUpdate(prevProps, prevState): Promise<void> {
    if (this.state.isLoading !== prevState.isLoading && this.state.config.id !== prevState.config.id) {
      // eslint-disable-next-line no-undef
      await Promise.all([this.getNextCase(), this.postReview(prevState.config)]);
    }
  }

  // API Call to GMC to get all the Cases and Feebacks for the Page
  async componentDidMount(): Promise<void> {
    // This disables the back button from functioning
    // It would put us in a spot where we would essentially be abandoning the case
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener('popstate', function() {
      window.history.pushState(null, document.title, window.location.href);
    });
    // eslint-disable-next-line no-undef
    await Promise.all([this.getNextCase(), this.postReview(undefined)]);
  }

  /// Helper function for creating the map with the Center Coordinates
  render(): JSX.Element {
    const { palette } = getTheme();
    const borderLeft = borderLeftStyle(palette);

    // Loading Spinner
    if (this.state == null || this.state.config === undefined || this.state.isLoading) {
      return (
        <div>
          <Spinner size={SpinnerSize.large} className="fixed-top w-100 h-100" />
        </div>
      );
    }

    // Handle service error
    if (this.state.isServiceError) {
      const emailBodyHref = formatEmailHref(
        'Case',
        this.state.config.tenant.displayName,
        this.state.config.title.displayName,
        this.state.config.subjectType,
        undefined,
        this.state.config.tenant.tenantId,
        this.state.config.title.titleId,
        undefined,
        this.state.errorMessage
      );

      return this.renderMessageBar(
        MessageBarType.error,
        <div>
          Unable to fetch case. Refresh page and if the issue persists,<a href={emailBodyHref}>submit feedback</a>.
        </div>
      );
    }

    // Get all the information needed for the current case
    if (this.state.cases.length === 0) {
      return this.renderMessageBar(MessageBarType.error, <div>No more cases in this queue. Return to dashboard.</div>);
    }

    const headerText = `${this.state.config.tenant.displayName} ${this.state.config.title.displayName} ${this.state.config.displayName} Review`;
    return (
      <ReviewContextProvider>
        <Stack className="h-100">
          <StackItem>
            <TitleBar headerText={headerText} />
          </StackItem>
          <StackItem grow>
            <Stack horizontal className="h-100">
              <StackItem grow>
                <NextCaseModal
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={async (): Promise<void> => {
                    await Promise.all([this.getNextCase(), this.postReview(undefined)]);
                  }}
                  multipleCases={this.state.cases.length > 1}
                  isEnabled={this.state.isTimerExpired}
                />
                <ReviewContent
                  cases={this.state.cases}
                  config={this.state.config}
                  onTimerExpired={this.onTimerExpired}
                />
              </StackItem>
              <StackItem style={borderLeft}>
                <ReviewOpinionForm
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onSubmitAndNextClick={this.onSubmitAndNext}
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onSubmitAndExitClick={this.onSubmitAndExit}
                  disableSubmit={this.state.isLoading || this.state.submittingReview} // Don't allow submit while page is loading or review is being posted
                  labelsData={this.state.config.categorizedTraits}
                  labelsPrompt={this.state.config.uxPreferences.decide.labelsPrompt}
                  config={this.state.config}
                  moderatorFeedbackCommentOptional={this.state.config.moderatorFeedbackCommentOptional}
                />
              </StackItem>
            </Stack>
          </StackItem>
        </Stack>
      </ReviewContextProvider>
    );
  }

  // Handle timer expire event
  private onTimerExpired = (): void => this.setState({ isTimerExpired: true });

  // renderMessageBar: Renders message bar covering entire viewport.
  private renderMessageBar(messageBarType: MessageBarType, innerMessage: JSX.Element): JSX.Element {
    return (
      <MessageBar messageBarType={messageBarType} isMultiline={true} className="mx-auto my-4 w-50">
        {innerMessage}
      </MessageBar>
    );
  }

  // Update which queue moderator is viewing.
  private async postReview(lastConfig: QueueConfiguration | undefined): Promise<void> {
    try {
      let id: string | undefined = this.state.userStatDocId;
      if (lastConfig !== undefined && id !== undefined) {
        await deleteReviewer(lastConfig, id);
        id = undefined;
      }

      const userStatDoc = await postReviewer(this.state.config, id);
      this.setState({
        userStatDocId: userStatDoc.id
      });
    } catch (error) {
      this.setState({
        userStatDocId: undefined
      });
    }
  }

  // Obtain next case in the queue
  private async getNextCase(): Promise<Case[]> {
    try {
      if (!this.state.config) {
        return [];
      }

      this.setState({
        isTimerExpired: false,
        isLoading: true,
        cases: []
      });

      let cases: Case[];
      if (this.state.caseId) {
        cases = [await claimCase(this.state.config, this.state.caseId)];
      } else {
        cases = await assignCases(this.state.config);
      }

      this.setState({
        isTimerExpired: false,
        isLoading: false,
        caseId: undefined,
        cases
      });
      return cases;
    } catch (error) {
      this.setState({
        isTimerExpired: false,
        isLoading: false,
        isServiceError: true,
        errorMessage: (error as Error).message
      });
      return [];
    }
  }

  private createReleaseCaseRequestBody(config: QueueConfiguration, releaseData: ReleaseCaseData[]): ReleaseCaseRequest {
    return {
      tenantId: config.tenant.tenantId,
      titleId: config.title.titleId,
      subjectType: config.subjectType,
      data: releaseData
    };
  }

  private async startSubmit(review: CaseReview[]): Promise<void> {
    if (this.state.isTimerExpired) {
      return;
    }
    this.setState({
      submittingReview: true
    });

    const releaseData = review.map(element => {
      if (element.opinion === undefined) {
        throw new Error('Expected opinion on release');
      }
      return generateReleaseData(element.case, element.opinion, this.state.config);
    });

    await unassignCases(this.createReleaseCaseRequestBody(this.state.config, releaseData)).catch((error: Error) => {
      this.setState({
        submittingReview: false,
        errorMessage: error.message
      });

      throw error;
    });
  }

  private endSubmit = (): void => {
    this.setState({
      submittingReview: false
    });
  };

  private onSubmitAndExit = async (review: CaseReview[]): Promise<void> => {
    await this.startSubmit(review);

    if (this.state.userStatDocId) {
      await deleteReviewer(this.state.config, this.state.userStatDocId).then(() => {
        this.props.history.push('/');
        this.endSubmit();
      });
    } else {
      this.props.history.push('/');
    }
  };

  private onSubmitAndNext = async (review: CaseReview[]): Promise<void> => {
    await this.startSubmit(review);

    // TODO: Refresh review document
    await Promise.all([this.getNextCase(), this.postReview(undefined)]).then(() => {
      this.endSubmit();
    });
  };
}

export default withRouter(Review);
